// Klaviyo signup teaser control — repositions Klaviyo's default
// bottom-corner teaser as a left-edge filing tab and hides it once the
// user successfully subscribes (NOT on dismissal — users who decline the
// popup keep the tab so they can change their mind).
//
// Pairs with assets/klaviyo-teaser-styles.css. This file handles:
//   - Adding the .rude-klaviyo-tab class to Klaviyo's teaser positioning
//     ancestor when the teaser is mounted (re-applies if Klaviyo
//     unmounts and re-injects on subsequent navigation states).
//   - Reading localStorage on load to apply .rude-klaviyo-hidden to
//     <body> immediately if the user has previously subscribed on this
//     browser, before the teaser can paint visibly.
//   - Cross-checking Klaviyo's own state via
//     localStorage['klaviyoOnsite'].viewedForms.modal.disabledTeasers —
//     if Klaviyo itself has flagged the teaser as disabled (which can
//     happen after submission or via Klaviyo's own teaser-close
//     mechanism), we respect that and hide too. Belt and braces.
//   - Capturing Klaviyo's form lifecycle events (`klaviyoForms` on
//     window) and persisting the "subscribed" flag only on a successful
//     submission event. Close / dismiss events do NOT hide the tab.
//
// Why localStorage not sessionStorage: subscription is permanent across
// browser sessions and restarts. A user who subscribes today shouldn't
// see the teaser again tomorrow on the same browser. (Different browser
// or cleared storage — they will see it again; Klaviyo's server-side
// __kla_id cookie holds the canonical subscription state, but we don't
// query that.)
//
// Diagnostic logging is verbose by design — every klaviyoForms event is
// logged so we can identify the exact detail.type Klaviyo emits on a
// successful submission. The follow-up commit will narrow the condition
// to the verified event name and strip these logs.

(function() {
    var SUBSCRIBED_KEY = 'rude_klaviyo_subscribed';
    var KLAVIYO_STATE_KEY = 'klaviyoOnsite';
    var TAB_CLASS = 'rude-klaviyo-tab';
    var HIDDEN_CLASS = 'rude-klaviyo-hidden';
    var TEASER_SELECTOR = '[class*="Teaser-pointer"]';

    function readSubscribed() {
        try {
            return localStorage.getItem(SUBSCRIBED_KEY) === '1';
        } catch (_) {
            return false;
        }
    }

    function writeSubscribed() {
        try {
            localStorage.setItem(SUBSCRIBED_KEY, '1');
        } catch (_) {}
    }

    // Defensive cross-check of Klaviyo's own state. After a successful
    // submission (or some other Klaviyo-internal teaser-disable path)
    // Klaviyo populates klaviyoOnsite.viewedForms.modal.disabledTeasers
    // with form IDs. If that object has any keys, the teaser is disabled
    // from Klaviyo's perspective; we respect that and hide.
    function klaviyoHasDisabledTeaser() {
        try {
            var raw = localStorage.getItem(KLAVIYO_STATE_KEY);
            if (!raw) return false;
            var parsed = JSON.parse(raw);
            var disabledTeasers = parsed
                && parsed.viewedForms
                && parsed.viewedForms.modal
                && parsed.viewedForms.modal.disabledTeasers;
            if (!disabledTeasers) return false;
            return Object.keys(disabledTeasers).length > 0;
        } catch (_) {
            return false;
        }
    }

    // Walk up from the teaser span to the first ancestor that's either
    // position: fixed or a direct child of <body>. That ancestor is the
    // element Klaviyo positions on screen — the right target for our
    // tab-style overrides.
    function findPositioningAncestor(teaser) {
        var el = teaser.parentElement;
        while (el && el !== document.body) {
            var cs = window.getComputedStyle(el);
            if (cs.position === 'fixed') return el;
            if (el.parentElement === document.body) return el;
            el = el.parentElement;
        }
        return null;
    }

    function applyTabClass() {
        var teaser = document.querySelector(TEASER_SELECTOR);
        if (!teaser) return;
        var ancestor = findPositioningAncestor(teaser);
        if (!ancestor) return;
        if (ancestor.classList.contains(TAB_CLASS)) return;
        ancestor.classList.add(TAB_CLASS);
        console.log('[klaviyo-teaser] tab class applied to', ancestor);
    }

    function start() {
        if (readSubscribed() || klaviyoHasDisabledTeaser()) {
            document.body.classList.add(HIDDEN_CLASS);
            console.log('[klaviyo-teaser] hide on load — subscribed flag set or Klaviyo disabledTeasers populated');
        }

        // Try once in case Klaviyo had already mounted before our defer
        // script ran.
        applyTabClass();

        // Watch for Klaviyo to mount (or re-mount) the teaser. Observer
        // stays alive for the page lifetime — applyTabClass is idempotent
        // (no-op once the class is on the right ancestor) so the per-
        // mutation cost is one querySelector + ancestor walk-up + class
        // check. Cheap.
        if (typeof MutationObserver === 'function') {
            var observer = new MutationObserver(function() {
                applyTabClass();
            });
            observer.observe(document.body, { childList: true, subtree: true });
        }

        // Klaviyo dispatches form lifecycle events on window with
        // detail.type values like 'open' / 'close' / 'submit' /
        // 'embedFormSubmit' / 'formMounted' / 'subscribed'. v2: only
        // persist on a successful submission — close events of any kind
        // (No thanks, X, click outside) must NOT hide the tab, so users
        // who decline can change their mind later. v1's hide-on-close
        // logic was the bug this commit fixes.
        //
        // Likely event names for "submitted": 'submit', 'embedFormSubmit',
        // or 'subscribed'. The heuristic matches all three; the
        // diagnostic log captures the actual event shape so the follow-up
        // commit can narrow this to whichever Klaviyo emits.
        window.addEventListener('klaviyoForms', function(e) {
            console.log('[klaviyo-teaser]', e.type, e.detail);
            var detailType = e.detail && e.detail.type;
            if (detailType === 'submit' || detailType === 'embedFormSubmit' || detailType === 'subscribed') {
                writeSubscribed();
                document.body.classList.add(HIDDEN_CLASS);
                console.log('[klaviyo-teaser] successful submission — tab hidden permanently for this browser');
            }
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
